import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Search, ReceiptText, BookOpen } from "lucide-react";
import { userApi } from "@/lib/api";
import { fmtMoney, fmtDate, TXN_LABELS, METHOD_TYPE_LABEL } from "@/lib/format";
import { PageHeader, StatusBadge, EmptyState, useDebounced } from "@/components/common";
import DepositDetailsSheet from "@/components/DepositDetailsSheet";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";

const STATUSES = ["Pending", "Under Review", "Approved", "Rejected", "Failed", "Refunded"];
const PAGE = 20;

export default function Transactions() {
  const [sp, setSp] = useSearchParams();
  const tab = sp.get("tab") || "deposits";
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [currency, setCurrency] = useState("all");
  const [page, setPage] = useState(0);
  const [openDep, setOpenDep] = useState(null);
  const s = useDebounced(search, 350);
  const params = { search: s || undefined, currency: currency === "all" ? undefined : currency, limit: PAGE, skip: page * PAGE };

  const depQ = useQuery({
    queryKey: ["deposits", "list", s, status, currency, page],
    queryFn: () => userApi.get("/deposits", { params: { ...params, status: status === "all" ? undefined : status } }).then((r) => r.data),
    enabled: tab === "deposits", refetchInterval: 20000,
  });
  const ledQ = useQuery({
    queryKey: ["ledger", "list", s, currency, page],
    queryFn: () => userApi.get("/wallet/transactions", { params }).then((r) => r.data),
    enabled: tab === "ledger",
  });
  const q = tab === "deposits" ? depQ : ledQ;
  const total = q.data?.total || 0;

  return (
    <div>
      <PageHeader title="Transactions" subtitle="Every deposit request and wallet ledger entry, searchable." />
      <Tabs value={tab} onValueChange={(v) => { setSp({ tab: v }); setPage(0); }} className="mb-4">
        <TabsList>
          <TabsTrigger value="deposits" className="gap-1.5" data-testid="transactions-tab-deposits"><ReceiptText className="h-4 w-4" /> Deposit requests</TabsTrigger>
          <TabsTrigger value="ledger" className="gap-1.5" data-testid="transactions-tab-ledger"><BookOpen className="h-4 w-4" /> Wallet ledger</TabsTrigger>
        </TabsList>
      </Tabs>
      <div className="mb-4 flex flex-col gap-2 sm:flex-row">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }} placeholder={tab === "deposits" ? "Search request ID, reference or method" : "Search reference or description"} className="h-10 bg-white pl-9" data-testid="transactions-search-input" />
        </div>
        {tab === "deposits" && (
          <Select value={status} onValueChange={(v) => { setStatus(v); setPage(0); }}>
            <SelectTrigger className="h-10 bg-white sm:w-44" data-testid="transactions-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All statuses</SelectItem>{STATUSES.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
          </Select>
        )}
        <Select value={currency} onValueChange={(v) => { setCurrency(v); setPage(0); }}>
          <SelectTrigger className="h-10 bg-white sm:w-36" data-testid="transactions-currency-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All currencies</SelectItem><SelectItem value="INR">INR</SelectItem><SelectItem value="USDT">USDT</SelectItem></SelectContent>
        </Select>
      </div>

      {q.isLoading ? <Skeleton className="h-64 rounded-xl" /> : q.isError ? <p className="text-sm text-rose-700">Could not load. <button className="underline" onClick={() => q.refetch()}>Retry</button></p> : total === 0 ? (
        <EmptyState title="Nothing found" testId="transactions-empty">Try a different search or filter.</EmptyState>
      ) : tab === "deposits" ? (
        <>
          <div className="space-y-2 md:hidden">
            {depQ.data.items.map((d) => (
              <button key={d.id} onClick={() => setOpenDep(d.id)} className="w-full rounded-xl border bg-white p-4 text-left" data-testid="deposit-history-card">
                <div className="flex items-center justify-between"><p className="num font-semibold">{fmtMoney(d.amount, d.currency)}</p><StatusBadge status={d.status} /></div>
                <p className="mt-1 text-xs text-muted-foreground">{d.payment_method_name} · {d.currency}</p>
                <div className="mt-2 flex justify-between text-xs text-muted-foreground"><span className="num">{d.id}</span><span>{fmtDate(d.created_at)}</span></div>
              </button>
            ))}
          </div>
          <Card className="hidden md:block">
            <Table>
              <TableHeader><TableRow><TableHead>Request ID</TableHead><TableHead>Method</TableHead><TableHead>Currency</TableHead><TableHead className="text-right">Amount</TableHead><TableHead>Reference</TableHead><TableHead>Status</TableHead><TableHead>Submitted</TableHead></TableRow></TableHeader>
              <TableBody>
                {depQ.data.items.map((d) => (
                  <TableRow key={d.id} className="cursor-pointer hover:bg-muted/60" onClick={() => setOpenDep(d.id)} data-testid="deposit-history-row">
                    <TableCell className="num text-xs">{d.id}</TableCell>
                    <TableCell>{d.payment_method_name}<span className="block text-xs text-muted-foreground">{METHOD_TYPE_LABEL[d.method_type]}</span></TableCell>
                    <TableCell>{d.currency}</TableCell>
                    <TableCell className="num text-right font-medium">{fmtMoney(d.amount, d.currency)}</TableCell>
                    <TableCell className="num text-xs">{d.transaction_reference}</TableCell>
                    <TableCell><StatusBadge status={d.status} /></TableCell>
                    <TableCell className="text-xs text-muted-foreground">{fmtDate(d.created_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </>
      ) : (
        <Card>
          <div className="overflow-x-auto scrollbar-thin">
            <Table>
              <TableHeader><TableRow><TableHead>Type</TableHead><TableHead>Reference</TableHead><TableHead className="text-right">Amount</TableHead><TableHead className="hidden text-right sm:table-cell">Balance after</TableHead><TableHead>Date</TableHead></TableRow></TableHeader>
              <TableBody>
                {ledQ.data.items.map((t) => (
                  <TableRow key={t.id} data-testid="ledger-row">
                    <TableCell><p className="text-sm font-medium">{TXN_LABELS[t.transaction_type] || t.transaction_type}</p><p className="hidden max-w-[260px] truncate text-xs text-muted-foreground sm:block">{t.description}</p></TableCell>
                    <TableCell className="num text-xs">{t.reference_id}</TableCell>
                    <TableCell className={`num text-right font-medium ${t.amount >= 0 ? "text-emerald-700" : "text-rose-700"}`}>{t.amount >= 0 ? "+" : "−"}{fmtMoney(Math.abs(t.amount), t.currency)}</TableCell>
                    <TableCell className="num hidden text-right text-xs sm:table-cell">{t.balance_after != null ? fmtMoney(t.balance_after, t.currency) : "—"}</TableCell>
                    <TableCell className="whitespace-nowrap text-xs text-muted-foreground">{fmtDate(t.created_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}
      {total > PAGE && (
        <div className="mt-4 flex items-center justify-between text-sm">
          <span className="text-muted-foreground">{page * PAGE + 1}–{Math.min(total, (page + 1) * PAGE)} of {total}</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(page - 1)} data-testid="transactions-prev-page">Previous</Button>
            <Button variant="outline" size="sm" disabled={(page + 1) * PAGE >= total} onClick={() => setPage(page + 1)} data-testid="transactions-next-page">Next</Button>
          </div>
        </div>
      )}
      <DepositDetailsSheet id={openDep} onClose={() => setOpenDep(null)} />
    </div>
  );
}
